import { FC, useContext, useEffect, useRef, useState } from 'react';

import { Button, CirclePlusIcon } from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { AuthContext } from 'auth';
import { CashboxContext, useIsCashboxEnabled } from 'cashbox';
import { FeatureHOC } from 'features';
import { LocalizationContext } from 'localization';
import {
  DepositModal,
  CashOrderModal,
  BookingModal,
  ManagerWarehouseModal,
  ClientRegisterModal,
} from 'modals';
import { SettingsContext } from 'settings';
import { TCashOrderType } from 'types';

import s from './FastAccess.module.scss';

export const FastAccess: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: { settings },
  } = useContext(SettingsContext);
  const isCashboxEnabled = useIsCashboxEnabled();
  const ref = useRef<HTMLInputElement>(null);

  const { printXReport } = useContext(CashboxContext);

  const [isReportPrinting, setIsReportPrinting] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  const [cashOrderModalType, setCashOrderModalType] =
    useState<TCashOrderType | null>(null);
  const [isWarehouseMWOpen, setIsWarehouseMWOpen] = useState(false);
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isClientRegisterOpen, setIsClientRegisterOpen] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsVisible(false);
      }
    };

    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setIsVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isVisible]);

  return (
    <div className={s.root} id="#fast_access" ref={ref}>
      <Button
        className={s['header-button']}
        variant="ghost"
        onClick={() => {
          setIsVisible((prev) => !prev);
        }}
      >
        <CirclePlusIcon
          className={cn(isVisible && s.active)}
          size="m"
          color="link-info"
        />
      </Button>
      {isVisible && (
        <div
          className={s.menu}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {cashOrderModalType && (
            <CashOrderModal
              type={cashOrderModalType}
              onClose={() => setCashOrderModalType(null)}
            />
          )}
          {isClientRegisterOpen && (
            <ClientRegisterModal
              onClose={() => setIsClientRegisterOpen(false)}
            />
          )}
          {isWarehouseMWOpen && (
            <ManagerWarehouseModal
              onClose={() => setIsWarehouseMWOpen(false)}
              onlyAdd
            />
          )}
          {isDepositOpen && (
            <DepositModal
              defaultClientUUID={defaultClient}
              onClose={() => {
                setIsBookingOpen(false);
                setIsDepositOpen(false);
              }}
            />
          )}
          {isBookingOpen && !isDepositOpen && (
            <BookingModal
              setIsDepositOpen={setIsDepositOpen}
              setIsDefaultClient={setIsDefaultClient}
              onClose={() => {
                setIsBookingOpen(false);
              }}
              selectedTime={dayjs().format('HH:mm')}
            />
          )}
          {permissions?.has('payment_create') && (
            <Button
              variant="ghost"
              className={s.button}
              onClick={() => {
                setIsDepositOpen(!isDepositOpen);
              }}
              iconName="WalletIcon"
            >
              {t('deposit.fill')}
            </Button>
          )}
          {permissions?.has('booking_manage') && (
            <FeatureHOC
              alias="booking"
              strategy="disable"
              tooltipPosition="right"
              name={t('booking')}
            >
              <Button
                variant="ghost"
                className={s.button}
                onClick={() => {
                  setIsBookingOpen(true);
                }}
                iconName="CalendarIcon"
              >
                {t('bookings.book')}
              </Button>
            </FeatureHOC>
          )}
          {permissions?.has('cash_order_create') && (
            <FeatureHOC
              alias="cash_orders"
              strategy="disable"
              name={t('orders.income')}
              tooltipPosition="right"
            >
              <Button
                onClick={() => setCashOrderModalType('PKO')}
                variant="ghost"
                className={s.button}
                iconName="IncomeIcon"
              >
                {t('orders.income')}
              </Button>
            </FeatureHOC>
          )}
          {isCashboxEnabled && (
            <Button
              onClick={() => {
                setIsReportPrinting(true);

                printXReport().finally(() => setIsReportPrinting(false));
              }}
              variant="ghost"
              className={s.button}
              disabled={isReportPrinting}
              iconName="XReportIcon"
            >
              {t('workShifts.reports.X')}
            </Button>
          )}
          {permissions?.has('cash_order_create') && (
            <FeatureHOC
              alias="cash_orders"
              strategy="disable"
              name={t('orders.outcome')}
              tooltipPosition="right"
            >
              <Button
                onClick={() => setCashOrderModalType('RKO')}
                variant="ghost"
                className={s.button}
                iconName="ConsumptionIcon"
              >
                {t('orders.outcome')}
              </Button>
            </FeatureHOC>
          )}
          {permissions?.has('good_manage_add') && (
            <FeatureHOC
              alias="stock_refill"
              strategy="disable"
              name={t('warehouse.add')}
              tooltipPosition="right"
            >
              <Button
                onClick={() => setIsWarehouseMWOpen(true)}
                variant="ghost"
                className={s.button}
                iconName="BarCodeIcon"
                disabled={!permissions?.has('shop_browse')}
              >
                {t('warehouse.add')}
              </Button>
            </FeatureHOC>
          )}
          {settings.clientRegister && (
            <Button
              onClick={() => {
                setIsClientRegisterOpen(true);
              }}
              variant="ghost"
              className={s.button}
              iconName="CirclePlusIcon"
            >
              {t('client.create')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
